import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import {
  modelChoice,
  modelGroupLabel,
  modelsWith,
  providerLabel,
  searchModels,
  type ModelChoice,
} from '@/session/models';
import { useSession } from '@/session/store';

const LISTBOX_ID = 'model-picker-listbox';
const optionId = (index: number) => `model-picker-option-${index}`;

/**
 * Picks the provider and model a session signs in on, as one value: the two are named together in the
 * handshake, and a model id means nothing without the provider whose id it is. The first row names
 * neither, which signs in without `metadata` and leaves the backend on its own default.
 *
 * A filtered list rather than a native select: nineteen ids, several of them forty characters of
 * Bedrock inference profile, are found by typing "haiku" or "nova pro" far faster than by reading a
 * system dropdown — and a native one cannot be dressed to look like the rest of the page anyway.
 *
 * Editable during a session on purpose: picking a model and reconnecting is how a tester moves between
 * them. What the open session is actually running is shown in the top bar.
 */
export function ModelPicker({ className, id }: { className?: string; id?: string }) {
  const modelId = useSession((state) => state.modelId);
  const setModelId = useSession((state) => state.setModelId);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = modelChoice(modelId);
  // A pinned model this build's catalog does not list is still offered, under its own provider.
  const catalog = useMemo(() => modelsWith(selected), [selected]);
  const matches = useMemo(() => searchModels(query, catalog), [query, catalog]);
  // Clamped rather than stored back: the list shrinks as the query grows, and an index past its end
  // would point at nothing on the keystroke that selects.
  const active = matches.length === 0 ? -1 : Math.min(activeIndex, matches.length - 1);

  function choose(choice: ModelChoice): void {
    setModelId(choice.id);
    setOpen(false);
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Every opening starts from the whole catalog, with the current model highlighted.
        if (next) {
          setQuery('');
          setActiveIndex(
            Math.max(
              0,
              catalog.findIndex((choice) => choice.id === modelId),
            ),
          );
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          className={cn('flex w-full items-center gap-2 text-left', className)}
        >
          <span className="min-w-0 flex-1 truncate">{selected?.label ?? modelId}</span>
          {selected?.kind === 'model' && (
            <span className="text-faint shrink-0 text-[10px] tracking-widest uppercase">
              {providerLabel(selected.provider)}
            </span>
          )}
          <ChevronsUpDown className="text-faint size-3.5 shrink-0" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          // Wider than the trigger when the trigger is narrow, so a Bedrock id still fits.
          className="border-line bg-surface z-50 w-[max(var(--radix-popover-trigger-width),22rem)] overflow-hidden rounded-xl border font-sans shadow-2xl"
        >
          <div className="border-line-soft flex items-center gap-2 border-b px-3 py-2">
            <Search className="text-faint size-3.5 shrink-0" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (matches.length === 0) return;
                  const step = event.key === 'ArrowDown' ? 1 : -1;
                  setActiveIndex((current) => {
                    const from = Math.min(current, matches.length - 1);
                    return (from + step + matches.length) % matches.length;
                  });
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  const choice = matches[active];
                  if (choice) choose(choice);
                }
              }}
              role="combobox"
              aria-expanded
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={active < 0 ? undefined : optionId(active)}
              aria-label="Filter models"
              placeholder="Filter by provider, family or id"
              spellCheck={false}
              autoComplete="off"
              className="text-text placeholder:text-faint min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <ul id={LISTBOX_ID} role="listbox" className="scroll-slim max-h-72 overflow-y-auto py-1">
            {matches.map((choice, index) => (
              <Option
                key={choice.id}
                choice={choice}
                index={index}
                active={index === active}
                selected={choice.id === modelId}
                // The group heading belongs to the first choice of each group, which is also how the
                // filtered list keeps its headings without recomputing the grouping.
                heading={
                  index === 0 || modelGroupLabel(matches[index - 1]) !== modelGroupLabel(choice)
                }
                onHover={() => setActiveIndex(index)}
                onSelect={() => choose(choice)}
              />
            ))}

            {matches.length === 0 && (
              <li className="text-muted px-3 py-6 text-center text-xs">
                No model matches “{query.trim()}”.
              </li>
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Option({
  choice,
  index,
  active,
  selected,
  heading,
  onHover,
  onSelect,
}: {
  choice: ModelChoice;
  index: number;
  active: boolean;
  selected: boolean;
  heading: boolean;
  onHover: () => void;
  onSelect: () => void;
}) {
  const row = useRef<HTMLLIElement>(null);

  // Keyboard navigation has to bring its target into view; the scroll container is the DOM's, not
  // React's, so this is the one thing here that reaches for it.
  useEffect(() => {
    if (active) row.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <>
      {heading && (
        <li
          role="presentation"
          className="text-faint px-3 pt-2 pb-1 text-[10px] font-semibold tracking-widest uppercase"
        >
          {modelGroupLabel(choice)}
        </li>
      )}
      {/* The ARIA combobox pattern keeps the keyboard on the input — the options are never focused,
          and their id is what `aria-activedescendant` points at — so the click handler here has no
          keyboard listener beside it by design. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <li
        ref={row}
        id={optionId(index)}
        role="option"
        aria-selected={selected}
        onMouseEnter={onHover}
        onClick={onSelect}
        className={cn(
          'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
          active && 'bg-hover',
        )}
      >
        <Check
          className={cn('text-accent-soft size-3.5 shrink-0', !selected && 'invisible')}
          aria-hidden
        />
        <span className={cn('min-w-0 flex-1 truncate', selected ? 'text-text' : 'text-muted')}>
          {choice.label}
        </span>
        <span
          className={cn(
            'text-faint shrink-0 truncate text-[10px]',
            choice.kind === 'model' && 'font-mono',
          )}
        >
          {choice.kind === 'model' ? choice.model : 'backend picks the model'}
        </span>
      </li>
    </>
  );
}
